import { FC } from 'react';
import { NavLinkItem } from '@components/layout/Sidebar/components/NavLinkItem';
import { MessagesNavItem, PayoutRequestNavItem, RequestNavItem } from '@components/layout/Sidebar/components/customNavItems';
import { gettext, useGettext } from '@cranium/i18n';
import { IdentityTypeEnum } from '@/interfaces/brokrete';
import { usePermissions, useClientSettings, usePartnerData } from '@/shared/access';
import { get } from 'lodash';

import { useLocalStorage } from '@/core/Storage';
import styled from 'styled-components';
import { Tooltip } from 'antd';
import { useUnreadMessagesCountFetcher } from '@pages/Messages/hooks/useUnreadMessagesCountFetcher';

export interface MenuItem {
  name: string;
  regex?: RegExp;
  settings?: {
    visible?: boolean;
    preview?: boolean;
  };
  allowed: boolean;
  withProtectedRenderer?: IdentityTypeEnum[];
  component: JSX.Element;
}

export const SettingsManager: FC<{
  settings?: {
    visible?: boolean;
    preview?: boolean;
  };
}> = ({ children, settings }) => {
  if (!canShowBySettings(settings)) return null;
  if (!settings) return <>{children}</>;
  if (!Object(settings).hasOwnProperty('visible') && !Object(settings).hasOwnProperty('preview')) return <>{children}</>;
  if (settings?.visible) return <>{children}</>;
  return (
    <Tooltip placement={'right'} destroyTooltipOnHide title={gettext('Please contact support to enable this feature')}>
      <Disabled>{children}</Disabled>
    </Tooltip>
  );
};

const Disabled = styled.div`
  position: relative;
  &:after {
    position: absolute;
    inset: 0;
    content: '';
    background: var(--white);
    opacity: 0.5;
  }
  > * {
    pointer-events: none;
    touch-action: none;
    cursor: not-allowed;
    color: var(--gray-7) !important;
  }
`;

export function canShowBySettings(settings?: { visible?: boolean; preview?: boolean }) {
  if (!settings) return true;
  if (!Object(settings).hasOwnProperty('visible') && !Object(settings).hasOwnProperty('preview')) return true;
  return settings.visible || settings?.preview;
}

type RootLinkMenuItem = Omit<MenuItem, 'component' | 'name'> & {
  to: string;
  title: string;
  icon: string;
  badge?: string | number | null;
};

type SidebarStructure = Array<
  | { type: 'rootLink'; item: RootLinkMenuItem }
  | { type: 'single'; item: MenuItem }
  | { type: 'grouped'; title: string; icon: string; items: MenuItem[] }
>;

export const useSidebarStructure = (mobile?: boolean) => {
  const permissions = usePermissions();
  const settings = useClientSettings();
  const partnerData = usePartnerData();
  const { gettext } = useGettext();

  const unreadMessagesCount = useUnreadMessagesCountFetcher(partnerData?.id ? { partners: [{ id: partnerData?.id }] } : {});

  const { value } = useLocalStorage<string>(`token-${partnerData?.id || ''}`);
  const scheduleQuery = useLocalStorage<string>(`LAST_KNOWN_SCHEDULE_FILTERS-${partnerData?.id || ''}.v1`);

  const menuItems: Record<string, MenuItem> = {
    orders: {
      name: 'orders',
      regex: /^\/(?!.*finances\/orders).*orders.*/,
      settings: settings?.orders,
      allowed: get(permissions, 'orders.show'),
      component: <NavLinkItem iconClass="icon-Order" label={gettext('Orders')} path="/orders" userTipName="sidebar.orders" />
    },
    multiEquipmentOrders: {
      name: 'multiEquipmentOrders',
      settings: {
        visible: false,
        preview: true
      },
      allowed: get(permissions, 'orders.show'),
      component: (
        <NavLinkItem
          iconClass="icon-Multi-Equipter"
          label={gettext('Multi Equipment Order')}
          path="/undefined"
          userTipName="sidebar.orders"
        />
      )
    },

    schedule: {
      name: 'schedule',
      settings: settings?.scheduling,
      allowed: get(permissions, 'scheduling.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          iconClass="icon-Property-1Schedule"
          label={gettext('Schedule')}
          path={['/schedule', scheduleQuery.value].filter(Boolean).join('')}
          userTipName="sidebar.schedule"
        />
      )
    },
    crewSchedule: {
      name: 'crewSchedule',
      settings: settings?.scheduling,
      allowed: get(permissions, 'scheduling.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          iconClass="icon-crew-schedule"
          label={gettext('Crew Based  Schedule')}
          path={['/schedule/operators', scheduleQuery.value].filter(Boolean).join('')}
          userTipName="sidebar.schedule"
          exact
        />
      )
    },
    drivers: {
      name: 'drivers',
      settings: settings?.drivers,
      allowed: get(permissions, 'drivers.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem iconClass="icon-User1" label={gettext('Employee')} path="/operators" exact userTipName="sidebar.drivers" />
      )
    },
    driversTimeOffCalendar: {
      name: 'availability',
      settings: settings?.drivers,
      allowed: get(permissions, 'drivers.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          iconClass="icon-Calendar1"
          label={gettext('Time Off Calendar')}
          path="/availability/block-off"
          exact
          userTipName="sidebar.drivers"
        />
      )
    },
    driversCrews: {
      name: 'driversCrews',
      settings: {
        visible: false,
        preview: true
      },
      allowed: get(permissions, 'drivers.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem iconClass="icon-Team" label={gettext('Crews')} path="/undefined" exact userTipName="sidebar.drivers" />
      )
    },
    driversSafetyCompliance: {
      name: 'driversSafetyCompliance',
      settings: {
        visible: false,
        preview: true
      },
      allowed: get(permissions, 'drivers.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          iconClass="icon-Safety1"
          label={gettext('Safety & Compliance (basic)')}
          path="/undefined"
          exact
          userTipName="sidebar.drivers"
        />
      )
    },
    deliveries: {
      name: 'deliveries',
      settings: settings?.driver_tasks,
      allowed: get(permissions, 'driver_tasks.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          iconClass="icon-time-table"
          label={gettext('Deliveries')}
          path="/deliveries"
          userTipName="sidebar.deliveries"
        />
      )
    },
    trucks: {
      name: 'trucks',
      settings: settings?.trucks,
      allowed: get(permissions, 'trucks.show'),
      component: <NavLinkItem iconClass="icon-truck2" label={gettext('Trucks')} path="/trucks" userTipName="sidebar.trucks" />
    },
    financeOrders: {
      name: '/finances/orders',
      settings: settings?.finances,
      allowed: get(permissions, 'finances.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: <NavLinkItem iconClass="icon-Order" label={gettext('Orders')} path="/finances/orders" exact />
    },
    financeInvoices: {
      name: 'financeInvoices',
      settings: {
        visible: false,
        preview: true
      },
      allowed: get(permissions, 'finances.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: <NavLinkItem iconClass="icon-dot-dot-dot" label={gettext('Invoices')} path="/undefined" exact />
    },

    financeAutoInvoices: {
      name: 'financeAutoInvoices',
      settings: {
        visible: false,
        preview: true
      },
      allowed: get(permissions, 'finances.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: <NavLinkItem iconClass="icon-dot-dot-dot-1" label={gettext('Auto Invoice')} path="/undefined" exact />
    },

    financeLumpSumInvoicing: {
      name: 'financeLumpSumInvoicing',
      settings: {
        visible: false,
        preview: true
      },
      allowed: get(permissions, 'finances.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: <NavLinkItem iconClass="icon-dot-dot-dot-2" label={gettext('Lump Sum Invoicing')} path="/undefined" exact />
    },

    financeRevenueTracking: {
      name: 'financeRevenueTracking',
      settings: {
        visible: false,
        preview: true
      },
      allowed: get(permissions, 'finances.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: <NavLinkItem iconClass="icon-LineChart1" label={gettext('Revenue Tracking')} path="/undefined" exact />
    },

    financePayments: {
      name: '/finances/payments',
      settings: settings?.finances,
      allowed: get(permissions, 'finances.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: <NavLinkItem iconClass="icon-Credit-Account" label={gettext('Payments')} path="/finances/payments" exact />
    },

    financePaymentLinks: {
      name: 'financePaymentLinks',
      settings: settings?.finances,
      allowed: get(permissions, 'finances.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem iconClass="icon-Payment-Link" label={gettext('Grouped payments')} path="/finances/groupedPayments" exact />
      )
    },

    financeCharges: {
      name: '/finances/payouts',
      settings: settings?.finances,
      allowed: get(permissions, 'finances.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: <PayoutRequestNavItem />
    },

    financePurchaseOrders: {
      name: 'financePurchaseOrders',
      settings: settings?.finances,
      allowed: get(permissions, 'finances.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: <NavLinkItem iconClass="icon-Store" label={gettext('Payouts')} path="/finances/payouts" exact />
    },
    financeConfigurations: {
      name: 'financeConfigurations',
      settings: {
        visible: false,
        preview: true
      },
      allowed: get(permissions, 'finances.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: <NavLinkItem iconClass="icon-Control1" label={gettext('Configurations')} path="/undefined" exact />
    },

    tax: {
      name: 'tax',
      settings: settings?.taxes,
      allowed: get(permissions, 'taxes.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: <NavLinkItem iconClass="icon-dollar-card" label={gettext('Tax')} path="/tax" userTipName="sidebar.tax" />
    },
    projects: {
      name: 'projects',
      settings: settings?.projects,
      allowed: get(permissions, 'projects.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem iconClass="icon-project-1" label={gettext('Projects')} path="/projects" userTipName="sidebar.projects" />
      )
    },
    pallets: {
      name: 'pallets',
      settings: settings?.pallets,
      allowed: get(permissions, 'pallets.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: <NavLinkItem iconClass="icon-pallets" label={gettext('Pallets')} path="/pallets" userTipName="sidebar.pallets" />
    },
    products: {
      name: 'products',
      settings: settings?.products,
      allowed: get(permissions, 'products.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem iconClass="icon-Dollar" label={gettext('Pricing (Basic)')} path="/products" userTipName="sidebar.products" />
      )
    },
    productsCost: {
      name: 'product-costs',
      settings: settings?.products?.cost_prices,
      allowed: get(permissions, 'products.cost_prices.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: <NavLinkItem iconClass="icon-Calculator" label={gettext('Product Costs')} path="/product-costs" />
    },
    productsAdvanced: {
      name: 'productsAdvanced',
      settings: {
        visible: false,
        preview: true
      },
      allowed: get(permissions, 'products.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          iconClass="icon-DollarCircle-advanced"
          label={gettext('Pricing (Advanced)')}
          path="/undefined"
          userTipName="sidebar.products"
        />
      )
    },
    locations: {
      name: 'locations',
      settings: settings?.locations,
      allowed: get(permissions, 'locations.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          iconClass="icon-factory"
          label={gettext('Locations')}
          path="/locations/list"
          userTipName="sidebar.locations"
        />
      )
    },
    contractors: {
      name: 'contractors',
      settings: settings?.contractors,
      allowed: get(permissions, 'contractors.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          iconClass="icon-CreditCard-setting"
          label={gettext('Contractors')}
          path="/contractors"
          userTipName="sidebar.contractors"
        />
      )
    },
    contractorsCreditManagement: {
      name: 'contractorsCreditManagement',
      settings: {
        visible: false,
        preview: true
      },
      allowed: get(permissions, 'contractors.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          iconClass="icon-CreditCard-setting1"
          label={gettext('Credit Management')}
          path="/undefined"
          userTipName="sidebar.contractors"
        />
      )
    },
    contractorsContacts: {
      name: 'contractorsContacts',
      settings: {
        visible: false,
        preview: true
      },
      allowed: get(permissions, 'contractors.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          iconClass="icon-Re-Time"
          label={gettext('Contacts & Communication History')}
          path="/undefined"
          userTipName="sidebar.contractors"
        />
      )
    },
    contractorsTerms: {
      name: 'contractorsTerms',
      settings: {
        visible: false,
        preview: true
      },
      allowed: get(permissions, 'contractors.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          iconClass="icon-Exception-1"
          label={gettext('Terms & Conditions')}
          path="/undefined"
          userTipName="sidebar.contractors"
        />
      )
    },
    contractorsQuotes: {
      name: 'contractorsQuotes',
      settings: {
        visible: false,
        preview: true
      },
      allowed: get(permissions, 'contractors.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem iconClass="icon-Chat" label={gettext('Quotes')} path="/undefined" userTipName="sidebar.contractors" />
      )
    },
    contractorsLead: {
      name: 'contractorsLead',
      settings: {
        visible: false,
        preview: true
      },
      allowed: get(permissions, 'contractors.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          iconClass="icon-LineChart-copy"
          label={gettext('Lead/Deal Tracking')}
          path="/undefined"
          userTipName="sidebar.contractors"
        />
      )
    },
    messages: {
      name: 'messages',
      settings: settings?.messages,
      allowed: get(permissions, 'messages.show'),
      component: <MessagesNavItem />
    },

    widgets: {
      name: 'widgets',
      settings: settings?.widgets,
      allowed: get(permissions, 'widgets.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: <NavLinkItem iconClass="icon-widget" label={gettext('Widgets')} path="/widgets" userTipName="sidebar.widgets" />
    },
    requests: {
      name: 'requests',
      settings: settings?.requests,
      allowed: get(permissions, 'requests.show'),
      component: <RequestNavItem />
    },
    suppliers: {
      name: 'suppliers',
      settings: settings?.suppliers,
      allowed: get(permissions, 'suppliers.show'),
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem iconClass="icon-suppliers" label={gettext('Suppliers')} path="/suppliers" userTipName="sidebar.suppliers" />
      )
    },
    analytics: {
      name: 'analytics',
      // settings: settings?.analytics,
      allowed: true, // get(permissions, 'analytics.show') && !isEmpty(settings?.analytics?.redirect_to),
      component: (
        <NavLinkItem
          iconClass="icon-BarChart"
          path="/analytics"
          href={settings?.analytics?.redirect_to || ''}
          label={gettext('Basic')}
          // userTipName="sidebar.analytics"
        />
      )
    },
    analyticsAdvanced: {
      name: 'analyticsAdvanced',
      settings: {
        visible: false,
        preview: true
      },
      allowed: true, // get(permissions, 'analytics.show') && !isEmpty(settings?.analytics?.redirect_to),
      component: (
        <NavLinkItem
          iconClass="icon-BarChart-adv"
          path="/analytics/advanced"
          href={settings?.analytics?.redirect_to || ''}
          label={gettext('Advanced')}
          // userTipName="sidebar.analytics"
        />
      )
    },

    autoCollect: {
      name: 'portal',
      settings: settings?.portal,
      allowed: get(permissions, 'portal.show') && !!settings?.portal?.visible,
      component: (
        <NavLinkItem
          iconClass="icon-global"
          path=""
          href={`${process.env.REACT_APP_PORTAL_URL}?CORE_JWT=${value || ''}`}
          label={gettext('Portal')}
          userTipName="sidebar.portal"
        />
      )
    },
    inventorySettings: {
      name: 'inventory/settings',
      settings: settings?.inventory,
      allowed: get(settings, 'inventory.visible'),
      component: <NavLinkItem iconClass="icon-Setting" path="/inventory/settings" label={gettext('Settings')} />
    },
    inventoryTransactions: {
      name: 'inventory/transactions',
      settings: settings?.inventory,
      allowed: get(settings, 'inventory.visible'),
      component: (
        <NavLinkItem iconClass="icon-inventory" path="/inventory/transactions" label={gettext('Inventory transaction')} />
      )
    },
    inventoryDashboard: {
      name: 'inventory/dashboard',
      settings: settings?.inventory,
      allowed: get(settings, 'inventory.visible'),
      component: <NavLinkItem iconClass="icon-inventory" path="/inventory/dashboard" label={gettext('Inventory')} />
    },
    telematics: {
      name: 'telematics',
      settings: {
        visible: false,
        preview: true
      },
      allowed: true,
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          iconClass="icon-Telematics"
          label={gettext('+ Telematics')}
          path="/undefined"
          userTipName="sidebar.contractors"
        />
      )
    },
    fleetManagement: {
      name: 'fleetManagement',
      settings: {
        visible: false,
        preview: true
      },
      allowed: true,
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          iconClass="icon-LineChart-copy"
          label={gettext('+ Fleet Management')}
          path="/undefined"
          userTipName="sidebar.contractors"
        />
      )
    },
    productOptions: {
      name: 'productOptions',
      settings: {
        visible: false,
        preview: true
      },
      allowed: true,
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          iconClass="icon-Control-horizontal"
          label={gettext('Options')}
          path="/undefined"
          userTipName="sidebar.contractors"
        />
      )
    },
    productSettings: {
      name: 'productSettings',
      settings: {
        visible: false,
        preview: true
      },
      allowed: true,
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem iconClass="icon-Setting" label={gettext('Settings')} path="/undefined" userTipName="sidebar.contractors" />
      )
    },
    availabilitySettings: {
      name: 'availabilitySettings',
      settings: {
        visible: false,
        preview: true
      },
      allowed: true,
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem
          iconClass="icon-Availability"
          label={gettext('Availability')}
          path="/undefined"
          userTipName="sidebar.contractors"
        />
      )
    },
    bookingSettings: {
      name: 'bookingSettings',
      settings: {
        visible: false,
        preview: true
      },
      allowed: true,
      withProtectedRenderer: ['partner', 'super_admin'],
      component: (
        <NavLinkItem iconClass="icon-schedule1" label={gettext('Booking')} path="/undefined" userTipName="sidebar.contractors" />
      )
    }
  };

  const structure: SidebarStructure = [
    {
      type: 'grouped',
      title: gettext('Dispatch'),
      icon: 'Locations-menu',
      items: [
        menuItems.orders,
        //  menuItems.multiEquipmentOrders,
        menuItems.schedule,
        // menuItems.crewSchedule,
        menuItems.deliveries,
        menuItems.requests
      ]
    },

    {
      type: 'grouped',
      title: gettext('Finance'),
      icon: 'menu-finances',
      items: [
        menuItems.financeOrders,
        // menuItems.financeInvoices,
        // menuItems.financeAutoInvoices,
        // menuItems.financeLumpSumInvoicing,
        // menuItems.financeRevenueTracking,
        menuItems.financePayments,
        menuItems.financePaymentLinks,
        // menuItems.financeCharges,
        menuItems.financePurchaseOrders
        // menuItems.financeConfigurations
      ]
    },

    {
      type: 'rootLink',
      item: {
        title: gettext('Customers'),
        icon: 'Property-1Customers',
        settings: settings?.contractors,
        allowed: get(permissions, 'contractors.show'),
        withProtectedRenderer: ['partner', 'super_admin'],
        to: '/contractors'
      }
      // type: 'grouped',

      // items: [
      //   menuItems.contractorsCreditManagement,
      //   menuItems.contractorsContacts,
      //   menuItems.contractorsTerms,
      //   menuItems.contractorsQuotes,
      //   menuItems.contractorsLead
      // ]
    },
    {
      type: 'grouped',
      title: gettext('Employees'),
      icon: 'Property-1Operators1',
      items: [
        menuItems.drivers

        //  menuItems.driversCrews,
        //  menuItems.driversSafetyCompliance
      ]
    },
    {
      type: 'rootLink',
      item: {
        settings: settings?.projects,
        allowed: get(permissions, 'projects.show'),
        withProtectedRenderer: ['partner', 'super_admin'],

        title: gettext('Projects'),
        icon: 'projectsmenu',

        to: '/projects'
      }
    },
    {
      type: 'rootLink',
      item: {
        title: gettext('Equipment'),
        icon: 'Property-1Trucks1',
        settings: settings?.trucks,
        allowed: get(permissions, 'trucks.show'),
        to: '/trucks'
      }
    },
    // {
    //   type: 'grouped',
    //   title: gettext('Equipment'),
    //   icon: 'Property-1Trucks1',
    //   items: [menuItems.telematics, menuItems.fleetManagement]
    // },
    {
      type: 'grouped',
      title: gettext('Products'),
      icon: 'Property-1Products_21',
      items: [
        menuItems.products,
        menuItems.productsAdvanced,
        menuItems.inventoryDashboard,
        menuItems.inventoryTransactions,
        menuItems.inventorySettings,
        menuItems.productsCost
        // menuItems.productOptions,
        // menuItems.productSettings,
      ]
    },
    {
      type: 'grouped',
      title: gettext('Reports'),
      icon: 'Property-1Finance1',
      items: [
        menuItems.autoCollect
        // menuItems.analytics,
        // menuItems.analyticsAdvanced
      ]
    },
    {
      type: 'grouped',
      title: gettext('Settings'),
      icon: 'Property-1Settings1',
      items: [
        // menuItems.availabilitySettings,
        // menuItems.bookingSettings
        menuItems.locations,
        menuItems.tax,
        menuItems.widgets,
        menuItems.pallets,
        menuItems.suppliers,
        menuItems.driversTimeOffCalendar
      ]
    },

    {
      type: 'rootLink',
      item: {
        title: gettext('Messages'),
        icon: 'messages-emu',
        settings: settings?.messages,
        allowed: get(permissions, 'messages.show'),
        to: '/messages',
        badge: unreadMessagesCount
      }
    }
  ];

  return structure;
};
